/**
 * Countdown Timer Custom Element
 * Enhanced Announcement Bar - Countdown Timer Feature
 * Version 1.2.2
 *
 * Displays a real-time countdown to a specified end date/time
 * Supports multiple display styles, timezone handling, and accessibility features
 * Includes visibility controls for days and seconds segments
 *
 * TIMEZONE HANDLING:
 * - Store timezone mode (default): All dates interpreted as Eastern Time (America/New_York)
 * - Automatically handles EST/EDT conversion (UTC-5 winter, UTC-4 summer)
 * - Ensures consistent countdown display for customers worldwide
 * - Example: "2025-12-31 23:59" = 11:59 PM Eastern, shown accurately for all timezones
 *
 * TODO: Improvements needed:
 * - Add fallback UI if JavaScript fails to load
 * - Add more robust date format validation and edge case handling
 * - Consider using Intl API or date library (luxon/date-fns) for timezone handling
 * - Add support for recurring countdowns
 * - Implement countdown completion callbacks/events
 * - Add animation when countdown expires
 * - Consider hiding announcement bar when countdown expires (optional)
 */
class CountdownTimer extends HTMLElement {
    constructor() {
        super();
        this.intervalId = null;
        this.endDate = null;
        this.isPaused = false;
        // TODO: Cache getAttribute calls here for better performance
        // this.styleType, this.label, this.hideDays, this.hideSeconds, etc.
    }

    connectedCallback() {
        // Parse attributes
        const endDateStr = this.getAttribute('end-date');
        const useLocalTz = this.getAttribute('use-local-tz') === 'true';

        if (!endDateStr) {
            this.renderExpired();
            return;
        }

        // Parse the end date (format: YYYY-MM-DD HH:MM)
        this.endDate = this.parseEndDate(endDateStr, useLocalTz);

        if (!this.endDate || isNaN(this.endDate.getTime())) {
            this.renderError();
            return;
        }

        // Initial render
        this.updateCountdown();

        // Update every second
        this.intervalId = setInterval(() => {
            if (!this.isPaused) {
                this.updateCountdown();
            }
        }, 1000);

        // Listen for carousel pause events (if parent announcement bar implements this)
        this.addEventListener('countdown-pause', () => this.pause());
        this.addEventListener('countdown-resume', () => this.resume());
    }

    disconnectedCallback() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        // TODO: Remove event listeners added in connectedCallback to prevent memory leaks
        // this.removeEventListener('countdown-pause', ...)
        // this.removeEventListener('countdown-resume', ...)
    }

    parseEndDate(dateStr, useLocalTz) {
        try {
            // Expected format: YYYY-MM-DD HH:MM
            const parts = dateStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/);

            if (!parts) {
                console.error('Invalid date format. Expected: YYYY-MM-DD HH:MM');
                return null;
            }

            const [, year, month, day, hours, minutes] = parts;

            if (useLocalTz) {
                // Parse as customer's local timezone
                return new Date(year, month - 1, day, hours, minutes);
            } else {
                // Parse as Eastern Time (America/New_York timezone)
                // Store is located in New York, all dates should be interpreted as ET
                // Eastern Time offset: EST = UTC-5, EDT = UTC-4 (DST from March to November)

                // Create a temporary date to check if DST is in effect for the target date
                const testDate = new Date(year, month - 1, day, 12, 0, 0);

                // Determine if DST is in effect for Eastern Time on this date
                // DST typically: 2nd Sunday in March to 1st Sunday in November
                const isDST = this.isEasternDST(testDate);
                const etOffset = isDST ? -4 : -5; // EDT = -4, EST = -5

                // Convert to UTC by adding the ET offset
                const utcDate = new Date(Date.UTC(
                    parseInt(year),
                    parseInt(month) - 1,
                    parseInt(day),
                    parseInt(hours) - etOffset, // Adjust hours to UTC
                    parseInt(minutes)
                ));

                return utcDate;
            }
        } catch (error) {
            console.error('Error parsing date:', error);
            return null;
        }
    }

    isEasternDST(date) {
        // Check if a given date falls within Eastern DST period
        // DST: 2nd Sunday in March at 2:00 AM to 1st Sunday in November at 2:00 AM
        // TODO: Simplify this using Intl.DateTimeFormat API:
        // const formatter = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' });
        // Check if formatted string contains 'EDT' or 'EST'
        const year = date.getFullYear();
        const month = date.getMonth(); // 0-indexed

        // DST is roughly March (month 2) through October (month 9)
        if (month < 2 || month > 10) return false; // Jan, Feb, Dec = no DST
        if (month > 2 && month < 10) return true;   // Apr-Oct = DST

        // For March and November, need to check specific Sunday
        const day = date.getDate();

        if (month === 2) { // March
            // Find 2nd Sunday: day 8-14
            const firstDay = new Date(year, 2, 1).getDay();
            const secondSunday = firstDay === 0 ? 8 : 15 - firstDay;
            return day >= secondSunday;
        }

        if (month === 10) { // November
            // Find 1st Sunday: day 1-7
            const firstDay = new Date(year, 10, 1).getDay();
            const firstSunday = firstDay === 0 ? 1 : 8 - firstDay;
            return day < firstSunday;
        }

        return false;
    }

    updateCountdown() {
        // TODO: Use Intersection Observer to pause updates when countdown not visible
        // This would improve performance on pages with multiple countdowns
        const now = new Date();
        const timeRemaining = this.endDate - now;

        if (timeRemaining <= 0) {
            this.renderExpired();
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
            // Dispatch expiration event
            this.dispatchEvent(new CustomEvent('countdown-expired', {
                bubbles: true,
                detail: { element: this }
            }));
            return;
        }

        const timeUnits = this.calculateTimeUnits(timeRemaining);
        this.renderCountdown(timeUnits);
    }

    calculateTimeUnits(ms) {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        // When days are hidden, show total hours instead of hours % 24
        const hideDays = this.getAttribute('hide-days') === 'true';

        return {
            days: days,
            hours: hideDays ? hours : hours % 24,
            minutes: minutes % 60,
            seconds: seconds % 60
        };
    }

    renderCountdown(timeUnits) {
        // TODO: Throttle ARIA label updates (e.g., every 10 seconds) to reduce screen reader noise
        const style = this.getAttribute('style-type') || 'boxed';
        const label = this.getAttribute('label') || '';

        let html = '<div class="countdown-timer__wrapper">';

        if (label) {
            html += `<span class="countdown-timer__label">${this.escapeHtml(label)}</span>`;
        }

        html += '<div class="countdown-timer__segments">';

        switch (style) {
            case 'boxed':
                html += this.renderBoxedStyle(timeUnits);
                break;
            case 'colon':
                html += this.renderColonStyle(timeUnits);
                break;
            case 'compact':
                html += this.renderCompactStyle(timeUnits);
                break;
            case 'text':
                html += this.renderTextStyle(timeUnits);
                break;
            default:
                html += this.renderBoxedStyle(timeUnits);
        }

        html += '</div></div>';

        this.innerHTML = html;
        this.setAttribute('aria-label', this.getAriaLabel(timeUnits));
    }

    renderBoxedStyle(time) {
        // TODO: Consider using template literals instead of string concatenation for better readability
        let html = '';

        html += '<span class="countdown-timer__days-wrapper">';
        html += this.createBoxedSegment(time.days, 'Days', 'd');
        html += '<span class="countdown-timer__separator">:</span>';
        html += '</span>';

        html += this.createBoxedSegment(time.hours, 'Hours', 'h');
        html += '<span class="countdown-timer__separator">:</span>';
        html += this.createBoxedSegment(time.minutes, 'Minutes', 'm');

        html += '<span class="countdown-timer__seconds-wrapper">';
        html += '<span class="countdown-timer__separator">:</span>';
        html += this.createBoxedSegment(time.seconds, 'Seconds', 's');
        html += '</span>';

        return html;
    }

    createBoxedSegment(value, fullUnit, shortUnit) {
        const paddedValue = String(value).padStart(2, '0');
        return `<div class="countdown-timer__segment"><span class="countdown-timer__value">${paddedValue}</span><span class="countdown-timer__unit">${shortUnit}</span></div>`;
    }

    renderColonStyle(time) {
        let html = '';

        html += '<span class="countdown-timer__days-wrapper">';
        html += this.createColonSegment(time.days, 'd');
        html += '<span class="countdown-timer__separator">:</span>';
        html += '</span>';

        html += this.createColonSegment(time.hours, 'h');
        html += '<span class="countdown-timer__separator">:</span>';
        html += this.createColonSegment(time.minutes, 'm');

        html += '<span class="countdown-timer__seconds-wrapper">';
        html += '<span class="countdown-timer__separator">:</span>';
        html += this.createColonSegment(time.seconds, 's');
        html += '</span>';

        return html;
    }

    createColonSegment(value, unit) {
        const paddedValue = String(value).padStart(2, '0');
        return `<span class="countdown-timer__segment"><span class="countdown-timer__value">${paddedValue}</span><span class="countdown-timer__unit">${unit}</span></span>`;
    }

    renderCompactStyle(time) {
        let html = '';

        html += `<span class="countdown-timer__days-wrapper"><span class="countdown-timer__segment"><span class="countdown-timer__value">${time.days}</span><span class="countdown-timer__unit">d</span></span> </span>`;
        html += `<span class="countdown-timer__segment"><span class="countdown-timer__value">${time.hours}</span><span class="countdown-timer__unit">h</span></span> `;
        html += `<span class="countdown-timer__segment"><span class="countdown-timer__value">${time.minutes}</span><span class="countdown-timer__unit">m</span></span>`;
        html += `<span class="countdown-timer__seconds-wrapper"> <span class="countdown-timer__segment"><span class="countdown-timer__value">${time.seconds}</span><span class="countdown-timer__unit">s</span></span></span>`;

        return html;
    }

    renderTextStyle(time) {
        const dayText = time.days === 1 ? 'day' : 'days';
        const hourText = time.hours === 1 ? 'hour' : 'hours';
        const minuteText = time.minutes === 1 ? 'minute' : 'minutes';
        const secondText = time.seconds === 1 ? 'second' : 'seconds';

        let html = '';
        html += `<span class="countdown-timer__days-wrapper"><span class="countdown-timer__segment"><span class="countdown-timer__value">${time.days}</span> <span class="countdown-timer__unit">${dayText}</span></span><span class="countdown-timer__separator">,</span> </span>`;
        html += `<span class="countdown-timer__segment"><span class="countdown-timer__value">${time.hours}</span> <span class="countdown-timer__unit">${hourText}</span></span><span class="countdown-timer__separator">,</span> `;
        html += `<span class="countdown-timer__segment"><span class="countdown-timer__value">${time.minutes}</span> <span class="countdown-timer__unit">${minuteText}</span></span>`;
        html += `<span class="countdown-timer__seconds-wrapper"><span class="countdown-timer__separator">,</span> <span class="countdown-timer__segment"><span class="countdown-timer__value">${time.seconds}</span> <span class="countdown-timer__unit">${secondText}</span></span></span>`;

        return html;
    }

    renderExpired() {
        const label = this.getAttribute('label') || '';
        this.innerHTML = `<div class="countdown-timer__wrapper countdown-timer--expired">${label ? `<span class="countdown-timer__label">${this.escapeHtml(label)}</span>` : ''}<span class="countdown-timer__expired-text">Expired</span></div>`;
        this.setAttribute('aria-label', 'Countdown expired');
        this.classList.add('countdown-timer--expired');
    }

    renderError() {
        this.innerHTML = `<div class="countdown-timer__wrapper countdown-timer--error"><span class="countdown-timer__error-text">Invalid date format</span></div>`;
        this.setAttribute('aria-label', 'Countdown error');
        this.classList.add('countdown-timer--error');
    }

    getAriaLabel(time) {
        const hideDays = this.getAttribute('hide-days') === 'true';
        const hideSeconds = this.getAttribute('hide-seconds') === 'true';
        let parts = [];

        if (!hideDays && time.days > 0) {
            parts.push(`${time.days} ${time.days === 1 ? 'day' : 'days'}`);
        }

        parts.push(`${time.hours} ${time.hours === 1 ? 'hour' : 'hours'}`);
        parts.push(`${time.minutes} ${time.minutes === 1 ? 'minute' : 'minutes'}`);

        if (!hideSeconds) {
            parts.push(`${time.seconds} ${time.seconds === 1 ? 'second' : 'seconds'}`);
        }

        return parts.join(', ') + ' remaining';
    }

    escapeHtml(text) {
        // TODO: Consider using DOMPurify or native sanitization for better security
        // Current implementation is basic but functional for simple text escaping
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    pause() {
        this.isPaused = true;
    }

    resume() {
        this.isPaused = false;
    }
}

// Register the custom element
if (!customElements.get('countdown-timer')) {
    customElements.define('countdown-timer', CountdownTimer);
}
